import React, { useState } from "react";
import { toast } from "sonner";
import { Bookmark, BookmarkPlus, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

const KEY = "members-saved-searches";
const load = () => { try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; } };
const PRESETS = [
  { name: "In arrears", q: "balance:>0", filter: "all" },
  { name: "Pending approval", q: "", filter: "pending" },
  { name: "Missing email", q: "", filter: "no_login" },
];

const slug = (s) => s.toLowerCase().replace(/\W+/g, "-");

function SearchChip({ s, active, onApply, onRemove }) {
  return (
    <span className={`group inline-flex items-center overflow-hidden rounded-full border text-xs transition-colors ${active ? "border-crimson bg-crimson text-parchment" : "border-gold/40 bg-white/60 hover:bg-gold-light"}`}>
      <button onClick={() => onApply(s)} className="px-2.5 py-1" data-testid={`saved-search-${slug(s.name)}`}>{s.name}</button>
      {onRemove && <button onClick={() => onRemove(s.name)} title="Remove" className="pr-2 opacity-0 transition-opacity group-hover:opacity-100" data-testid={`saved-search-remove-${slug(s.name)}`}><X size={11} /></button>}
    </span>
  );
}

export function SavedSearches({ current, onApply }) {
  const [saved, setSaved] = useState(load);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const persist = (list) => { setSaved(list); localStorage.setItem(KEY, JSON.stringify(list)); };
  const save = () => {
    const list = [...saved.filter((s) => s.name !== name.trim()), { name: name.trim(), ...current }];
    persist(list); setOpen(false); setName(""); toast.success(`Saved “${name.trim()}”`);
  };
  const remove = (n) => persist(saved.filter((s) => s.name !== n));
  const isActive = (s) => s.q === current.q && s.filter === current.filter && (s.sort || "") === (current.sort || "");
  const canSave = current.q || current.filter !== "all";
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="saved-searches">
      <Bookmark size={14} className="text-gold" />
      {PRESETS.map((s) => <SearchChip key={s.name} s={s} active={isActive(s)} onApply={onApply} />)}
      {saved.map((s) => <SearchChip key={s.name} s={s} active={isActive(s)} onApply={onApply} onRemove={remove} />)}
      {canSave && <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs text-crimson" onClick={() => setOpen(true)} data-testid="saved-search-add"><BookmarkPlus size={13} /> Save this search</Button>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Save search</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Query: <span className="font-mono-accent">{current.q || "—"}</span> · Filter: {current.filter}{current.sort ? ` · Sort: ${current.sort}` : ""}</p>
          <Input autoFocus placeholder="e.g. Worcester in arrears" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && name.trim() && save()} data-testid="saved-search-name" />
          <DialogFooter><Button className="bg-crimson" disabled={!name.trim()} onClick={save} data-testid="saved-search-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
